import React, { useCallback, useMemo, useState, memo } from 'react';
import AnimatedItem from './AnimatedItem';
import '../styles/Partners.css';

/** All partner logos under public/albums/partnersLogo */
const PARTNERS = [
  {
    name: 'CHT Wedding Planner',
    logo: '/albums/partnersLogo/eventPlanners/CHTWeddingPlannerLogo.jpg',
    href: 'https://www.facebook.com/CHT.Wedding.Planner',
    category: 'Event Planning',
  },
  {
    name: 'Jum Ful Event Management',
    logo: '/albums/partnersLogo/eventPlanners/JumFulLogo.png',
    href: 'https://www.facebook.com/jumfuleventmanagement',
    category: 'Event Planning',
  },
  {
    name: 'Full Bareng',
    logo: '/albums/partnersLogo/eventPlanners/FullBarengLogo2.png',
    href: 'https://www.facebook.com',
    category: 'Event Planning',
  },
  {
    name: "Uthai's Glam",
    logo: "/albums/partnersLogo/makeOverArtists/Print for Uthai's Glam logo.png",
    href: 'https://www.facebook.com/glamwithUthai',
    category: 'Beauty & Styling',
  },
  {
    name: 'Bebi Makeup',
    logo: '/albums/partnersLogo/makeOverArtists/BebiLogo1.png',
    href: 'https://www.facebook.com',
    category: 'Beauty & Styling',
  },
  {
    name: 'Achen Burmese Store',
    logo: '/albums/partnersLogo/dress/AchenLogo.png',
    href: 'https://www.facebook.com/achenburmesstore',
    category: 'Wedding Attire',
  },
  {
    name: 'Aryeshwari Boutique',
    logo: '/albums/partnersLogo/dress/AryeshwariBoutiquePerabahLogo.png',
    href: 'https://www.facebook.com',
    category: 'Wedding Attire',
  },
  {
    name: 'Yi Yi',
    logo: '/albums/partnersLogo/dress/YiYiLogo.webp',
    href: 'https://www.facebook.com',
    category: 'Wedding Attire',
  },
];

/** Put all unique partners in a single row (no duplicates) */
function chunkPartnersIntoRows(partners, rowCount = 1) {
  if (rowCount === 1) {
    const seen = new Set();
    const row = [];
    partners.forEach((partner) => {
      if (seen.has(partner.name)) return;
      seen.add(partner.name);
      row.push(partner);
    });
    return row.length ? [row] : [];
  }

  const rows = Array.from({ length: rowCount }, () => []);
  const seen = Array.from({ length: rowCount }, () => new Set());

  partners.forEach((partner, index) => {
    const rowIndex = index % rowCount;
    if (seen[rowIndex].has(partner.name)) return;
    seen[rowIndex].add(partner.name);
    rows[rowIndex].push(partner);
  });

  partners.forEach((partner) => {
    rows.forEach((row, rowIndex) => {
      if (row.length >= 4) return;
      if (seen[rowIndex].has(partner.name)) return;
      seen[rowIndex].add(partner.name);
      row.push(partner);
    });
  });

  return rows.filter((row) => row.length > 0);
}

const PartnerTile = memo(({ partner }) => (
  <a
    href={partner.href}
    target="_blank"
    rel="noopener noreferrer"
    className="partners-tile"
    title={partner.name}
    aria-label={`Visit ${partner.name} - ${partner.category}`}
  >
    <span className="partners-tile-logo-wrap">
      <img
        src={partner.logo}
        alt=""
        className="partners-tile-logo"
        loading="lazy"
        decoding="async"
        draggable={false}
      />
    </span>
    <span className="partners-tile-name">{partner.name}</span>
    <span className="partners-tile-category">{partner.category}</span>
  </a>
));

PartnerTile.displayName = 'PartnerTile';

const PartnerRow = memo(({ items, rowIndex, direction, duration }) => {
  const [paused, setPaused] = useState(false);
  const onEnter = useCallback(() => setPaused(true), []);
  const onLeave = useCallback(() => setPaused(false), []);

  const renderTiles = (suffix) =>
    items.map((partner, i) => (
      <PartnerTile key={`${suffix}-${rowIndex}-${partner.name}-${i}`} partner={partner} />
    ));

  return (
    <div
      className={`partners-row partners-row--${direction}${paused ? ' is-paused' : ''}`}
      style={{ '--marquee-duration': `${duration}s` }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div className="partners-row-viewport">
        <div className="partners-row-track">
          {renderTiles('a')}
          {renderTiles('b')}
        </div>
      </div>
    </div>
  );
});

PartnerRow.displayName = 'PartnerRow';

const Partners = () => {
  const rows = useMemo(() => chunkPartnersIntoRows(PARTNERS, 1), []);

  return (
    <section className="partners-section" aria-labelledby="partners-heading">
      <div className="partners-header">
        <AnimatedItem delay={0.1} direction="up">
          <div className="home-section-head partners-section-head">
            <div>
              <p className="home-eyebrow">Network</p>
              <h2 id="partners-heading" className="home-section-title">
                Our Trusted <span className="dancing-script">Partners</span>
              </h2>
            </div>
            <p className="home-section-subtitle">
              We provide whole solutions for your wedding - from dress to makeover to event management
            </p>
          </div>
        </AnimatedItem>
      </div>

      <div className="partners-rows" aria-label="Partner logos">
        {rows.map((items, rowIndex) => (
          <PartnerRow
            key={`partners-row-${rowIndex}`}
            items={items}
            rowIndex={rowIndex}
            direction="ltr"
            duration={42}
          />
        ))}
      </div>

      <div className="partners-footer">
        <AnimatedItem delay={0.2} direction="up">
          <div className="partners-note">
            <div className="partners-note-content">
              <h4>Complete Wedding Solutions</h4>
              <p>
                Beyond photography and films, we connect you with trusted partners for dress,
                makeover, and event management - so every part of your day is covered under one
                coordinated team.
              </p>
              <a href="/book" className="partners-cta-btn">
                Plan Your Complete Wedding
              </a>
            </div>
          </div>
        </AnimatedItem>
      </div>
    </section>
  );
};

export default Partners;
